import { gsap, WF_EASE } from './gsap'

/**
 * Home hero load (a-15 desktop / a-47 mobile): hero words tilt up out of the
 * page, the glow blooms, then the iPhone rises and the nav pill fades in.
 */
export const setupHomeHeroLoad = (root: HTMLElement): void => {
    if (!root.querySelector('.hero-text-wrapper')) return

    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
    const xOff = isMobile ? 10 : 50

    gsap.set('.iphone-holder', { yPercent: 120, autoAlpha: 0 })
    gsap.set('.iphone-drop-shadow', { autoAlpha: 0 })
    // The blur radius stays fixed (set in CSS) so Safari rasterises it once and
    // composites on the GPU. The glow blooms in via opacity only, which never
    // re-rasterises the blur, instead of animating filter: blur() radius, which
    // collapses Safari to a few frames per second.
    gsap.set('.hero-text-blur', { autoAlpha: 0 })
    gsap.set('.hero-text-wrapper._01', { transformPerspective: 1000, transformOrigin: '50% 50%', scale: 1.1, autoAlpha: 0, rotateX: 60, x: -xOff })
    gsap.set('.hero-text-wrapper._02', { transformPerspective: 1000, transformOrigin: '50% 50%', scale: 1.1, autoAlpha: 0, rotateX: 60, x: xOff })

    const tl = gsap.timeline({ defaults: { ease: WF_EASE } })

    tl.to('.hero-text-wrapper._01', { rotateX: 0, autoAlpha: 1, duration: 1.1 }, 0)
        .to('.hero-text-wrapper._02', { rotateX: 0, autoAlpha: 1, duration: 1.1 }, 0.4)
        .to('.hero-text-blur', { autoAlpha: 0.6, duration: 1.8 }, 0.4)
        .to('.iphone-holder', { yPercent: 0, duration: 1.2 }, 1.2)
        .to('.iphone-holder', { autoAlpha: 1, duration: 0.6 }, 1.2)
        .to(['.hero-text-wrapper._01', '.hero-text-wrapper._02'], { x: 0, scale: 1, duration: 1.1 }, 1.2)
        .to('.iphone-drop-shadow', { autoAlpha: 1, duration: 1 }, 1.5)
}

/**
 * Inner-page staggered load (a-54): nav + flagged elements lift up, untwist on
 * the Y axis and fade in on a fixed cadence.
 */
export const setupStaggerLoad = (root: HTMLElement): void => {
    const items: Array<[string, number]> = [
        ['.animate-on-load-01', 0],
        ['.animate-on-load-02', 0.15],
        ['.animate-on-load-03', 0.3],
        ['.features-number._1', 0.45],
        ['.animate-on-load-04', 0.45],
        ['.features-number._2', 0.6],
        ['.features-number._3', 0.75],
        ['.features-number._4', 0.9],
        ['.features-phone-container', 1.05]
    ]

    const present = items.filter(([selector]) => root.querySelector(selector))
    if (!present.length) return

    gsap.set(present.map(([selector]) => selector), { transformPerspective: 1000, y: 30, rotateY: 32, autoAlpha: 0 })

    const tl = gsap.timeline({ defaults: { duration: 0.7, ease: WF_EASE } })
    present.forEach(([selector, position]) => tl.to(selector, { y: 0, rotateY: 0, autoAlpha: 1 }, position))
}

/**
 * Sign-up cards entrance (a-38 / a-39 / a-41 / a-42) — only the first cards grid;
 * the second grid uses the generic move-reveal.
 */
export const setupSignupCards = (root: HTMLElement): void => {
    if (!root.querySelector('.cards-grid')) return

    const cardEntrance = (
        card: Element | null | undefined,
        imageSelector: string | null,
        imageY: number,
        cardDelay: number,
        textDelay: number
    ): void => {
        if (!card) return

        const tl = gsap.timeline({
            scrollTrigger: { trigger: card as HTMLElement, start: 'top bottom', toggleActions: 'play none none none' }
        })

        gsap.set(card, { y: 30 })
        tl.to(card, { y: 0, duration: 1.2, ease: WF_EASE }, 0)

        const image = imageSelector ? card.querySelector<HTMLElement>(imageSelector) : null
        if (image) {
            gsap.set(image, { y: imageY, autoAlpha: 0, scale: 1.1 })
            tl.to(image, { y: 0, autoAlpha: 1, scale: 1, duration: 1.3, ease: WF_EASE }, cardDelay)
        }

        const texts = card.querySelectorAll<HTMLElement>('.black-text')
        if (texts.length) {
            gsap.set(texts, { autoAlpha: 0, y: 10 })
            tl.to(texts, { autoAlpha: 1, y: 0, duration: 1.3, ease: WF_EASE }, textDelay)
        }
    }

    cardEntrance(root.querySelector('.card-image-holder._01')?.closest('.card-background'), '.card-image-holder._01', -81, 0, 0.2)
    cardEntrance(root.querySelector('.card-image-holder._02')?.closest('.card-background'), '.card-image-holder._02', 81, 0.2, 0.4)
    cardEntrance(root.querySelector('.card-iphone-holder')?.closest('.card-background'), '.card-iphone-holder', 81, 0.4, 0.5)

    const logoHolder = root.querySelector<HTMLElement>('.card-logo-holder')
    if (logoHolder) {
        const logoContainer = logoHolder.querySelector<HTMLElement>('.card-logo-container')
        const tl = gsap.timeline({
            scrollTrigger: { trigger: logoHolder, start: 'top bottom', toggleActions: 'play none none none' }
        })

        gsap.set(logoHolder, { y: 30 })
        tl.to(logoHolder, { y: 0, duration: 1.2, ease: WF_EASE }, 0)

        if (logoContainer) {
            gsap.set(logoContainer, { autoAlpha: 0, scale: 1.3 })
            tl.to(logoContainer, { scale: 1, duration: 1.3, ease: 'back.inOut(1.7)' }, 0.6)
                .to(logoContainer, { autoAlpha: 1, duration: 0.5 }, 0.6)
        }
    }
}
